import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { QuoteItemSection } from '@paintscout/util/builder'
import { useTranslation } from '@paintscout/ui'
import { SquareMinusIcon, SquarePlusIcon } from '@paintscout/ui/icons'

/**
 * Deliberately a bare <button> rather than the shared MUI `Button`: this is a quiet inline link, and
 * MUI's chrome (the `.MuiButton-label` span, `startIcon` margins, and the `iconSizeSmall` font-size
 * rule) all had to be fought off one by one to get there. Resetting a native button is less code and
 * nothing overrides it later.
 */
const useStyles = makeStyles<Theme, { padTop?: boolean }>((theme: Theme) => ({
  root: {
    // native button reset
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: ({ padTop }) => `${padTop ? 4 : 0}px 0 0 0`,
    cursor: 'pointer',
    textAlign: 'inherit',
    height: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',

    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.paintscout.palette.brand.Neutral.P60,
    '&:hover': {
      color: theme.paintscout.palette.brand.Neutral.P35
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2
    }
  },
  // SvgIcon sizes off font-size and is display:block, so it has no baseline/leading of its own —
  // `align-items: center` on the row lines it up with the text directly.
  icon: {
    fontSize: theme.typography.pxToRem(14)
  }
}))

export interface OptionsApprovalButtonProps {
  section: QuoteItemSection
  padTop?: boolean
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

export default function OptionsApprovalButton({ onClick, section, padTop }: OptionsApprovalButtonProps) {
  const classes = useStyles({ padTop })
  const { __ } = useTranslation()
  const isRemove = section === 'added-options'
  const Icon = isRemove ? SquareMinusIcon : SquarePlusIcon

  return (
    <button type="button" className={classes.root} onClick={onClick}>
      <Icon className={classes.icon} />
      {isRemove ? __('Remove') : __('Add option')}
    </button>
  )
}
